import * as React from 'react';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';

import { TableListingPage, IBasicTableColumn, DangerAlert, IconTextButton, HorizontalMenu, Typography } from 'ui-lib';
import { getErrorMessage, isNullOrEmpty } from 'common-utils';

import {
    useAccountConfigurationStore,
    useAccountStore,
    useAuthStore,
    useRoutesStore,
    useUserStore,
} from '../../../../store';
import { AccountType } from '../../../../dto/access-management/account-dto';
import { deleteAccount } from '../utils';
import { buildNavigatePath } from '../../utils';
import { ACCOUNT_ROUTE_CODES } from './index';
import { useSearchableTableStates } from '../../components/table-utils';

import manageAccountCss from './account.css';
import { IDisplayAccountOptions } from '../../../../store/account-store';
import { usePromiseMemo } from 'utils/common-utils';
import { isUndefined } from 'lodash';
import { usePermission } from 'hooks/use-permission.hook';
import { ADMIN_PERMISSIONS } from 'contexts/permissions/permission-provider';


export const ManageAccountsComponent = observer(() => {
    const { hasPermission } = usePermission({
        permissionGroups: ['Admin'],
    });

    const { currentUser } = useAuthStore();
    const accountStore = useAccountStore();
    const accountConfigurationStore = useAccountConfigurationStore();
    const userStore = useUserStore();
    const navigate = useNavigate();

    const routeStore = useRoutesStore();
    const addRoute = routeStore.getRoute(ACCOUNT_ROUTE_CODES.ADD);
    const editRoute = routeStore.getRoute(ACCOUNT_ROUTE_CODES.EDIT);
    const externalRepoRoute = routeStore.getRoute(ACCOUNT_ROUTE_CODES.EXTERNAL_REPOSITORY_SETTINGS);
    const { onFilter, filteredDataSource } = useSearchableTableStates(accountStore.displayAccounts, ['name']);
    const [errorMessage, setErrorMessage] = React.useState<string>(null);

    const hiddenAccountSettings = usePromiseMemo(undefined, async () => {
        try {
            return !(await accountConfigurationStore.getExternalVisibility(currentUser.accountId));
        } catch (err) {
            return true;
        }
    }, []);
    const loading = React.useMemo(() => {
        if (isUndefined(hiddenAccountSettings)) return true;
        return accountStore.dataLoading;
    }, [accountStore.dataLoading, hiddenAccountSettings]);

    React.useEffect(() => {
        setErrorMessage(accountStore.error);
    }, [accountStore.error]);

    const onAdd = () => navigate(addRoute.path);
    const onDelete = async (id: number) => {
        await accountStore.deleteAccount(id).catch((err) => setErrorMessage(getErrorMessage(err)));
        if (!isNullOrEmpty(userStore.displayUsers)) await userStore.loadUsers(true);
    };

    const columns: IBasicTableColumn[] = [
        {
            code: 'name',
            title: 'Account name',
            dataIndex: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name),
            render: hasPermission(ADMIN_PERMISSIONS.ACCOUNTS_MODIFY)
                ? (text: string, record: any) => (
                      <IconTextButton
                          onClick={() => navigate(buildNavigatePath(editRoute.path, { ':id': record.id }))}
                          label={text}
                          type='link'
                      />
                  )
                : undefined,
        },
        {
            code: 'type',
            title: 'Account type',
            dataIndex: 'type',
        },
        {
            code: 'displayEnvironmentNames',
            title: 'Environments',
            dataIndex: 'displayEnvironmentNames',
        },
        ...(hasPermission(ADMIN_PERMISSIONS.ACCOUNTS_MODIFY)
            ? [
                  {
                      code: 'action',
                      title: 'Action',
                      dataIndex: 'action',
                      render: (_, record: IDisplayAccountOptions) => (
                          <div className={manageAccountCss.tableActions}>
                              <HorizontalMenu
                                  className={manageAccountCss.horizontalMenu}
                                  hidden={hiddenAccountSettings}
                                  menuItems={[
                                      {
                                          code: 'actionsMenu',
                                          label: () => 'Settings',
                                          subMenuItems: [
                                              {
                                                  code: 'external-repo',
                                                  iconName: 'toolbox',
                                                  label: () => 'External repository',
                                                  onClick: () =>
                                                      navigate(
                                                          buildNavigatePath(externalRepoRoute.path, {
                                                              ':id': `${record.id}`,
                                                          }),
                                                      ),
                                              },
                                          ],
                                          onClick: () => undefined,
                                      },
                                  ]}
                              />
                              <Typography.Link
                                  className={manageAccountCss.deleteButton}
                                  disabled={record.type != AccountType.CLIENT}
                                  onClick={() => deleteAccount(record.id, onDelete)}
                              >
                                  Delete
                              </Typography.Link>
                          </div>
                      ),
                  },
              ]
            : []),
    ];

    return (
        <React.Fragment>
            {errorMessage ? <DangerAlert message={errorMessage} className={manageAccountCss.alert} /> : null}
            <TableListingPage
                title=''
                tableProps={{
                    title: 'All accounts',
                    loadingState: loading,
                    table: {
                        columns: columns,
                        dataSource: filteredDataSource,
                    },
                    toolbar: {
                        searchPlaceholder: 'Search accounts',
                        onSearch: onFilter,
                        onAdd: currentUser.accountType !== AccountType.CLIENT ? onAdd : undefined,
                        onReload: () => accountStore.getAccounts(true),
                    },
                }}
            />
        </React.Fragment>
    );
});
